import React, { useState } from 'react';
import { X, Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const resolveFeedbackEndpoint = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (!apiUrl) {
    return '/api/feedback';
  }

  const trimmed = apiUrl.replace(/\/+$/, '');
  const baseUrl = trimmed.endsWith('/v1') ? trimmed.slice(0, -3) : trimmed;
  return `${baseUrl}/feedback`;
};

const FEEDBACK_TYPES = [
  { value: 'feedback', label: 'Just some feedback', emoji: '💬' },
  { value: 'bug', label: "I've found a bug", emoji: '🐛' },
  { value: 'idea', label: 'I have an idea', emoji: '💡' },
  { value: 'other', label: 'Something else', emoji: '📝' },
];

export function FeedbackModal({ config, currentPath, onSubmit, onClose }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('feedback');
  const [name, setName] = useState('');
  const [wantsResponse, setWantsResponse] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const feedbackEndpoint = resolveFeedbackEndpoint();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !description.trim()) return;

    // Validate email if user wants a response
    if (wantsResponse && !email.trim()) {
      setErrorMessage('Please provide an email address to get a response.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    const feedbackData = {
      title: title.trim(),
      description: description.trim(),
      type,
      name: name.trim() || undefined,
      wantsResponse,
      email: wantsResponse ? email.trim() : undefined,
      page: currentPath,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      timestamp: new Date().toISOString(),
    };

    try {
      // Submit to API
      const response = await fetch(feedbackEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...feedbackData,
          github: config.github,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        const message = error.message || error.error || 'Failed to submit feedback';
        throw new Error(message);
      }

      setSubmitStatus('success');
      onSubmit(feedbackData);
      
      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedType = FEEDBACK_TYPES.find(t => t.value === type);

  return (
    <div className="mb-3 w-80 bg-white rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 flex items-center justify-between">
        <h3 className="font-semibold text-white">Share Your Feedback</h3>
        <button
          onClick={onClose}
          className="text-white/80 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Success State */}
      {submitStatus === 'success' && (
        <div className="p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h4 className="text-lg font-semibold text-neutral-900 mb-2">Thank You!</h4>
          <p className="text-sm text-neutral-600">Your feedback has been submitted successfully.</p>
        </div>
      )}

      {/* Form */}
      {submitStatus !== 'success' && (
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Type Selector */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700">
              What's this about?
            </label>
            <div className="relative">
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full appearance-none rounded-lg border border-neutral-300 px-4 py-2.5 pr-10 text-neutral-900 bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none transition-all"
              >
                {FEEDBACK_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.emoji} {t.label}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg className="w-5 h-5 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Name (Optional) */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700">
              Name <span className="text-xs text-neutral-500">(optional)</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 text-neutral-900 placeholder:text-neutral-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none transition-all"
              maxLength={100}
            />
          </div>

          {/* Title */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={
                type === 'bug' ? 'Brief description of the bug' :
                type === 'idea' ? 'What\'s your idea?' :
                'What\'s on your mind?'
              }
              className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 text-neutral-900 placeholder:text-neutral-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none transition-all"
              required
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                type === 'bug' ? 'What happened? What did you expect to happen?' :
                type === 'idea' ? 'Tell us more about your idea...' :
                'Share the details...'
              }
              rows={4}
              className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 text-neutral-900 placeholder:text-neutral-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none transition-all resize-none"
              required
              maxLength={2000}
            />
            <div className="text-xs text-neutral-400 text-right">
              {description.length}/2000
            </div>
          </div>

          {/* Get back to me checkbox */}
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={wantsResponse}
                onChange={(e) => setWantsResponse(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-neutral-300 text-violet-600 focus:ring-2 focus:ring-violet-500/20 focus:ring-offset-0 cursor-pointer"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-neutral-700 group-hover:text-neutral-900">
                  Get back to me on this
                </span>
                <p className="text-xs text-neutral-500 mt-0.5">
                  We'll follow up with you about this feedback
                </p>
              </div>
            </label>

            {/* Email field (conditional) */}
            {wantsResponse && (
              <div className="pl-7 space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                <label className="block text-sm font-medium text-neutral-700">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 text-neutral-900 placeholder:text-neutral-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none transition-all"
                  required={wantsResponse}
                />
              </div>
            )}
          </div>

          {/* Error Message */}
          {submitStatus === 'error' && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Page Context */}
          <div className="text-xs text-neutral-400 flex items-center gap-1">
            <span>From:</span>
            <code className="px-1.5 py-0.5 bg-neutral-100 rounded text-neutral-600">{currentPath}</code>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || !title.trim() || !description.trim()}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-violet-600 text-white font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Submit Feedback
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}

